/**
 * useBookingsEmbed Composable
 * Manages Microsoft Bookings iFrame embed state with 5-second timeout-first fallback.
 * Task: 113-111-T002
 */

import { ref, onMounted, onUnmounted } from 'vue'
import type { Ref } from 'vue'

export interface UseBookingsEmbedReturn {
  isLoaded: Ref<boolean>
  hasFailed: Ref<boolean>
  error: Ref<string | null>
  markLoaded: () => void
  triggerFallback: (errorMsg?: string) => void
}

const EMBED_TIMEOUT_MS = 5000

export function useBookingsEmbed(): UseBookingsEmbedReturn {
  const isLoaded = ref<boolean>(false)
  const hasFailed = ref<boolean>(false)
  const error = ref<string | null>(null)

  let embedTimeoutRef: ReturnType<typeof setTimeout> | null = null

  function markLoaded(): void {
    isLoaded.value = true
    if (embedTimeoutRef !== null) {
      clearTimeout(embedTimeoutRef)
      embedTimeoutRef = null
    }
  }

  function triggerFallback(errorMsg?: string): void {
    hasFailed.value = true
    if (errorMsg !== undefined) {
      error.value = errorMsg
    }
    if (embedTimeoutRef !== null) {
      clearTimeout(embedTimeoutRef)
      embedTimeoutRef = null
    }
  }

  onMounted(() => {
    embedTimeoutRef = setTimeout(() => {
      if (!isLoaded.value) {
        triggerFallback('Booking embed timed out after 5 seconds')
      }
    }, EMBED_TIMEOUT_MS)
  })

  onUnmounted(() => {
    if (embedTimeoutRef !== null) {
      clearTimeout(embedTimeoutRef)
      embedTimeoutRef = null
    }
  })

  return { isLoaded, hasFailed, error, markLoaded, triggerFallback }
}
